import { useEffect, useRef, useState } from 'react';
import { Store, Lock, Mail, ArrowRight, ArrowLeft, CheckCircle, User, Phone, MapPin, Eye, EyeOff } from 'lucide-react';
import type { User as UserType } from '../../App';
import { authAPI } from '../../lib/api';
import { getRestaurants } from '../../lib/mockData';

declare global {
  interface Window {
    google?: any;
  }
}

interface LoginPageProps {
  onLogin: (user: UserType) => void;
}

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M22 12.06C22 6.49 17.52 2 11.94 2 6.37 2 1.88 6.49 1.88 12.06c0 4.85 3.66 8.87 8.44 9.74v-6.9H7.9V12.06h2.42V9.9c0-2.4 1.43-3.72 3.62-3.72 1.05 0 2.15.19 2.15.19v2.36h-1.21c-1.19 0-1.56.74-1.56 1.5v1.83h2.66l-.43 2.84h-2.23v6.9c4.78-.87 8.44-4.9 8.44-9.74Z"
    />
  </svg>
);

const GoogleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M12.24 10.32v3.36h4.72c-.19 1.2-1.4 3.52-4.72 3.52-2.84 0-5.16-2.35-5.16-5.24s2.32-5.24 5.16-5.24c1.62 0 2.71.69 3.33 1.28l2.27-2.19C16.4 4.45 14.53 3.6 12.24 3.6 7.86 3.6 4.32 7.2 4.32 12s3.54 8.4 7.92 8.4c4.58 0 7.62-3.23 7.62-7.78 0-.52-.06-.92-.12-1.32H12.24Z"
    />
  </svg>
);

const TwitterIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M17.2 3h3.2l-7 8.01L21.7 21h-6.5l-5.1-6.36L4.5 21H1.3l7.5-8.58L1 3h6.7l4.6 5.74L17.2 3Zm-1.12 16h1.77L7.67 5h-1.9l10.31 14Z"
    />
  </svg>
);

const AUTH_MODE_PATHS = {
  'internal-login': '/sign-in',
  'customer-login': '/customer-sign-in',
  signup: '/customer-sign-up',
  'delivery-login': '/delivery-partner-sign-in',
  'delivery-signup': '/delivery-boy-apply',
} as const;

const getModeFromPath = (
  pathname: string
): 'internal-login' | 'customer-login' | 'signup' | 'delivery-login' | 'delivery-signup' => {
  switch (pathname) {
    case AUTH_MODE_PATHS['customer-login']:
      return 'customer-login';
    case AUTH_MODE_PATHS.signup:
      return 'signup';
    case AUTH_MODE_PATHS['delivery-signup']:
      return 'delivery-signup';
    case AUTH_MODE_PATHS['delivery-login']:
      return 'delivery-login';
    case AUTH_MODE_PATHS['internal-login']:
    default:
      return 'internal-login';
  }
};

const mapApiRoleToUiRole = (role?: string): UserType['role'] => {
  const normalized = String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  switch (normalized) {
    case 'SUPERADMIN':
    case 'SUPER_ADMIN':
      return 'superadmin';
    case 'RESTAURANTADMIN':
    case 'RESTAURANT_ADMIN':
    // case 'RESTAURANTADMIN':
      return 'restaurant_admin';
    case 'MANAGER':
      return 'manager';
    case 'KITCHENSTAFF':
    case 'KITCHEN_STAFF':
    // case 'KITCHENSTAFF':
      return 'staff';
    case 'DELIVERY_PARTNER':
    case 'DELIVERYPARTNER':
    case 'DELIVERY_PATNER':
      return 'delivery_partner';
    case 'CUSTOMER':
      return 'customer';
    default:
      return 'customer';
  }
};

export function LoginPage({ onLogin }: LoginPageProps) {
  const [mode, setMode] = useState<'internal-login' | 'customer-login' | 'signup' | 'delivery-login' | 'delivery-signup'>(
    () => getModeFromPath(window.location.pathname)
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [signupName, setSignupName] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupAddress, setSignupAddress] = useState('');
  const [deliveryName, setDeliveryName] = useState('');
  const [deliveryEmail, setDeliveryEmail] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [deliveryPassword, setDeliveryPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pendingFirstLoginUser, setPendingFirstLoginUser] = useState<any>(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [isForgotView, setIsForgotView] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeliveryPassword, setShowDeliveryPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const googleCodeClientRef = useRef<any>(null);
  const socialAuthHandledRef = useRef(false);

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (rememberMe && email.trim()) {
      localStorage.setItem('remembered_email', email.trim());
    } else if (!rememberMe) {
      localStorage.removeItem('remembered_email');
    }
  }, [rememberMe, email]);

  useEffect(() => {
    const handlePopState = () => {
      setMode(getModeFromPath(window.location.pathname));
      setError('');
      setInfo('');
      setIsForgotView(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const expectedPath = AUTH_MODE_PATHS[mode];
    if (window.location.pathname !== expectedPath) {
      window.history.replaceState({}, '', expectedPath);
    }
  }, [mode]);

  useEffect(() => {
    if (!googleClientId) return;

    const initializeGoogle = () => {
      if (!window.google?.accounts?.oauth2?.initCodeClient) return;
      googleCodeClientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: googleClientId,
        scope: 'openid email profile',
        ux_mode: 'popup',
        callback: async (response: { code?: string; error?: string }) => {
          if (!response?.code) {
            setError('Google sign in failed. Please try again.');
            return;
          }
          setError('');
          setInfo('');
          setIsLoading(true);
          try {
            const responseData = await authAPI.googleLogin({
              code: response.code,
              role: mode === 'delivery-login' ? 'DELIVERY_PARTNER' : 'CUSTOMER',
            });

            if (responseData.success && responseData.data?.user) {
              const apiUser = responseData.data.user as any;
              const role = mapApiRoleToUiRole(apiUser.role);
              const fallbackRestaurantId = getRestaurants()[0]?.id;
              const needsRestaurantContext = ['restaurant_admin', 'manager', 'staff'].includes(role);
              const resolvedRestaurantId =
                apiUser.restaurantId ||
                apiUser.restaurant_id ||
                apiUser.restaurant?.id ||
                (needsRestaurantContext ? fallbackRestaurantId : undefined);

              const userData = {
                ...apiUser,
                role,
                restaurantId: resolvedRestaurantId,
                token: responseData.data.token,
              };

              onLogin(userData);
            } else {
              setError('Google sign in failed. Please try again.');
            }
          } catch (err: any) {
            setError(err.message || 'Google sign in failed. Please try again.');
          } finally {
            setIsLoading(false);
          }
        },
      });
    };

    if (window.google?.accounts?.oauth2?.initCodeClient) {
      initializeGoogle();
      return;
    }

    const existingScript = document.getElementById('google-identity-sdk');
    if (existingScript) {
      existingScript.addEventListener('load', initializeGoogle, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-identity-sdk';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogle;
    script.onerror = () => {
      setError('Unable to load Google sign in. Please try again.');
    };
    document.body.appendChild(script);
  }, [googleClientId, mode, onLogin]);

  useEffect(() => {
    if (socialAuthHandledRef.current) return;

    const hash = window.location.hash.replace(/^#/, '');
    const search = window.location.search.replace(/^\?/, '');
    const params = new URLSearchParams(hash || search);
    const authToken = params.get('auth_token') || params.get('token');
    const refreshToken = params.get('refresh_token');
    const errorParam = params.get('error');
    const providerParam = params.get('provider');

    if (!authToken && !errorParam) return;

    socialAuthHandledRef.current = true;
    window.history.replaceState({}, '', window.location.pathname);

    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setInfo('');
      return;
    }

    if (!authToken) return;

    setIsLoading(true);
    setError('');
    setInfo(providerParam ? `Signing you in with ${providerParam}...` : 'Signing you in...');

    localStorage.setItem('auth_token', authToken);
    localStorage.setItem('token', authToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }

    const finalizeSocialLogin = async () => {
      try {
        const responseData = await authAPI.getCurrentUser();
        if (!responseData.success || !responseData.data) {
          throw new Error('Social sign in failed. Please try again.');
        }

        const apiUser = responseData.data as any;
        const role = mapApiRoleToUiRole(apiUser.role);
        const fallbackRestaurantId = getRestaurants()[0]?.id;
        const needsRestaurantContext = ['restaurant_admin', 'manager', 'staff'].includes(role);
        const resolvedRestaurantId =
          apiUser.restaurantId ||
          apiUser.restaurant_id ||
          apiUser.restaurant?.id ||
          (needsRestaurantContext ? fallbackRestaurantId : undefined);

        const userData = {
          ...apiUser,
          role,
          restaurantId: resolvedRestaurantId,
          token: authToken,
        };

        onLogin(userData);
      } catch (err: any) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setError(err.message || 'Social sign in failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    void finalizeSocialLogin();
  }, [onLogin]);

  const handleModeChange = (
    nextMode: 'internal-login' | 'customer-login' | 'signup' | 'delivery-login' | 'delivery-signup'
  ) => {
    setMode(nextMode);
    setError('');
    setInfo('');
    setIsForgotView(false);
    const nextPath = AUTH_MODE_PATHS[nextMode];
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, '', nextPath);
    }
  };

  const handleDeliveryApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setIsLoading(true);

    try {
      const response = await authAPI.register({
        name: deliveryName,
        email: deliveryEmail,
        phone: deliveryPhone,
        password: deliveryPassword,
        role: 'DELIVERY_PARTNER',
      });

      if (response.success) {
        setDeliveryName('');
        setDeliveryEmail('');
        setDeliveryPhone('');
        setDeliveryPassword('');
        setMode('delivery-login');
        setInfo('Delivery partner account created successfully. Please sign in to continue.');
      } else {
        setError('Unable to create delivery partner account.');
      }
    } catch (err: any) {
      setError(err.message || 'Delivery partner signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setIsLoading(true);

    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('currentUser');

      if (rememberMe) {
        localStorage.setItem('remembered_email', email);
      } else {
        localStorage.removeItem('remembered_email');
      }

      const response = await authAPI.login({ email, password });

      if (response.success && response.data?.user) {
        const apiUser = response.data.user as any;
        const role = mapApiRoleToUiRole(apiUser.role);
        const fallbackRestaurantId = getRestaurants()[0]?.id;
        const needsRestaurantContext = ['restaurant_admin', 'manager', 'staff'].includes(role);
        const resolvedRestaurantId =
          apiUser.restaurantId ||
          apiUser.restaurant_id ||
          apiUser.restaurant?.id ||
          (needsRestaurantContext ? fallbackRestaurantId : undefined);

        const userData = {
          ...apiUser,
          role,
          restaurantId: resolvedRestaurantId,
          token: response.data.token,
        };

        if (role === 'delivery_partner') {
          window.history.replaceState({}, '', '/delivery-partner-application');
          onLogin(userData);
        } else if ((response.data as any).mustResetPassword) {
          setPendingFirstLoginUser(userData);
          setInfo('First login detected. Please reset your password.');
        } else {
          onLogin(userData);
        }
      } else {
        setError('Invalid credentials');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setIsLoading(true);

    try {
      const response = await authAPI.register({
        name: signupName,
        email,
        phone: signupPhone || undefined,
        password,
        address: signupAddress,
        role: 'customer',
      });

      if (response.success) {
        setMode('customer-login');
        setSignupName('');
        setSignupPhone('');
        setSignupAddress('');
        setInfo('Account created successfully. Please sign in to open your customer dashboard.');
      } else {
        setError('Unable to create account.');
      }
    } catch (err: any) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!pendingFirstLoginUser) return;

    if (!newPassword || newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Confirm password does not match.');
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.resetPassword({
        currentPassword: password,
        newPassword,
        confirmPassword,
      });
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('currentUser');
      setPendingFirstLoginUser(null);
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setInfo('Password reset successful.');
    } catch (err: any) {
      setError(err.message || 'Unable to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const socialProviders = [
    { label: 'Facebook', envKey: 'VITE_FACEBOOK_AUTH_URL', Icon: FacebookIcon, textClass: 'text-blue-700' },
    { label: 'Google', envKey: 'VITE_GOOGLE_AUTH_URL', Icon: GoogleIcon, textClass: 'text-red-500' },
    { label: 'Twitter', envKey: 'VITE_TWITTER_AUTH_URL', Icon: TwitterIcon, textClass: 'text-sky-500' },
  ] as const;

  const handleProviderLogin = (provider: (typeof socialProviders)[number]) => {
    setError('');
    setInfo('');

    if (provider.label === 'Google') {
      if (!googleClientId) {
        setError('Google sign in is not configured. Missing VITE_GOOGLE_CLIENT_ID in .env.');
        return;
      }
      if (!googleCodeClientRef.current) {
        setError('Google sign in is still loading. Please try again in a moment.');
        return;
      }
      setInfo('Opening Google sign in...');
      googleCodeClientRef.current.requestCode();
      return;
    }

    const authUrl = import.meta.env[provider.envKey] as string | undefined;
    if (!authUrl) {
      setError(`${provider.label} sign in is not configured. Missing ${provider.envKey} in .env.`);
      return;
    }

    setInfo(`Redirecting to ${provider.label}...`);
    if (provider.label === 'Twitter') {
      const role = mode === 'delivery-login' ? 'DELIVERY_PARTNER' : 'CUSTOMER';
      try {
        const url = new URL(authUrl, window.location.origin);
        url.searchParams.set('role', role);
        window.location.href = url.toString();
        return;
      } catch {
        // Fallback to raw auth URL
      }
    }
    window.location.href = authUrl;
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfo('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter your email address to reset your password.');
      return;
    }

    setIsForgotLoading(true);
    try {
      const response = await authAPI.forgotPassword({ email });
      if (response.success) {
        setInfo(response.message || 'If an account exists, a password reset email has been sent.');
      } else {
        setError('Unable to send password reset email. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Unable to send password reset email. Please try again.');
    } finally {
      setIsForgotLoading(false);
    }
  };

  const title =
    pendingFirstLoginUser
      ? 'Reset Password'
      : isForgotView
        ? 'Forgot Password'
        : mode === 'signup'
          ? 'Customer Sign Up'
          : mode === 'delivery-signup'
            ? 'Delivery Partner Sign Up'
            : mode === 'customer-login'
              ? 'Customer Sign In'
              : mode === 'delivery-login'
                ? 'Delivery Partner Sign In'
                : 'Sign In';

  const subtitle =
    pendingFirstLoginUser
      ? 'Secure your account'
      : isForgotView
        ? 'Enter your email to reset your password'
        : mode === 'signup'
          ? 'Create your customer account'
          : mode === 'delivery-signup'
            ? 'Apply as a delivery partner'
            : mode === 'customer-login'
              ? 'Sign in to your customer account'
              : mode === 'delivery-login'
                ? 'Sign in and complete your profile in delivery dashboard'
                : 'Access your dashboard';

  return (
    <div className="auth-fixed-theme min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      <div className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                window.history.pushState({}, '', '/');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="flex items-center gap-3 text-left transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white rounded-xl"
              title="Go to Landing Page"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <Store className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-gray-900">FoodHub</h1>
                <p className="text-gray-600 text-sm">Complete Restaurant Management System</p>
              </div>
            </button>
            <div className="hidden md:flex items-center gap-2 text-sm">
              <div className="flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1.5 rounded-full">
                <CheckCircle className="w-4 h-4" />
                <span>Production Ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-gray-900 mb-4 text-4xl md:text-5xl">Manage Your Restaurant</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Multi-role platform with real-time updates and seamless order management
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 mb-12 max-w-2xl mx-auto">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-gray-900">{title}</h3>
                <p className="text-gray-600 text-sm">{subtitle}</p>
              </div>
            </div>

            <form
              onSubmit={
                isForgotView
                  ? (e) => {
                      e.preventDefault();
                      void handleForgotPassword();
                    }
                  : pendingFirstLoginUser
                    ? handleResetPassword
                    : mode === 'signup'
                      ? handleSignup
                      : mode === 'delivery-signup'
                        ? handleDeliveryApplication
                        : handleLogin
              }
              className="space-y-5"
            >
              {isForgotView ? (
                <>
                  <div className="bg-orange-50 border-2 border-orange-200 text-orange-800 px-4 py-3 rounded-xl">
                    Please enter your email address to reset your password.
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotView(false);
                      setError('');
                      setInfo('');
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-600 shadow-sm transition-all hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </button>
                </>
              ) : !pendingFirstLoginUser ? (
                <>
                  {mode === 'signup' && (
                    <div>
                      <label className="block text-gray-700 mb-2 font-medium">Full Name</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          value={signupName}
                          onChange={(e) => setSignupName(e.target.value)}
                          className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                          placeholder="Enter your full name"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {mode === 'delivery-signup' && (
                    <>
                      <div>
                        <label className="block text-gray-700 mb-2 font-medium">Full Name</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            value={deliveryName}
                            onChange={(e) => setDeliveryName(e.target.value)}
                            className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            placeholder="Enter your full name"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 mb-2 font-medium">Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="email"
                            value={deliveryEmail}
                            onChange={(e) => setDeliveryEmail(e.target.value)}
                            className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            placeholder="Enter your email"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 mb-2 font-medium">Phone Number</label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="tel"
                            value={deliveryPhone}
                            onChange={(e) => setDeliveryPhone(e.target.value)}
                            className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            placeholder="Enter your phone number"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 mb-2 font-medium">Password</label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type={showDeliveryPassword ? 'text' : 'password'}
                            value={deliveryPassword}
                            onChange={(e) => setDeliveryPassword(e.target.value)}
                            className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            placeholder="Enter your password"
                            required
                            minLength={6}
                          />
                          <button
                            type="button"
                            onClick={() => setShowDeliveryPassword((prev) => !prev)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label={showDeliveryPassword ? 'Hide password' : 'Show password'}
                          >
                            {showDeliveryPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {mode !== 'delivery-signup' && (
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>
                  )}

                  {mode === 'signup' && (
                    <>
                      <div>
                        <label className="block text-gray-700 mb-2 font-medium">Phone Number</label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="tel"
                            value={signupPhone}
                            onChange={(e) => setSignupPhone(e.target.value)}
                            className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                            placeholder="Enter your phone number"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 mb-2 font-medium">Delivery Address</label>
                        <div className="relative">
                          <MapPin className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                          <textarea
                            value={signupAddress}
                            onChange={(e) => setSignupAddress(e.target.value)}
                            className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all min-h-[104px] resize-none"
                            placeholder="Enter your address"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {mode !== 'delivery-signup' && (
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  )}

                    {(mode === 'internal-login' || mode === 'customer-login' || mode === 'delivery-login') && (
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-3 text-gray-700 text-sm">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        Remember me
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotView(true);
                          setError('');
                          setInfo('');
                        }}
                        className="text-sm font-medium text-orange-500 hover:text-orange-600"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="bg-amber-50 border-2 border-amber-200 text-amber-800 px-4 py-3 rounded-xl">
                    Please reset password.
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Enter new password"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">Confirm New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Confirm new password"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {error && <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl">{error}</div>}
              {info && <div className="bg-green-50 border-2 border-green-200 text-green-700 px-4 py-3 rounded-xl">{info}</div>}

              <button
                type="submit"
                disabled={isForgotView ? isForgotLoading : isLoading}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3.5 rounded-xl hover:from-orange-600 hover:to-red-700 transition-all font-medium shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(isForgotView ? isForgotLoading : isLoading) ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {isForgotView
                      ? 'Sending...'
                      : pendingFirstLoginUser
                        ? 'Updating Password...'
                        : mode === 'signup'
                          ? 'Creating Account...'
                          : mode === 'delivery-signup'
                            ? 'Creating Account...'
                          : 'Signing In...'}
                  </>
                ) : (
                  <>
                    {isForgotView
                      ? 'Send Reset Link'
                      : pendingFirstLoginUser
                        ? 'Reset Password'
                        : mode === 'signup'
                          ? 'Create Account'
                          : mode === 'delivery-signup'
                            ? 'Sign Up'
                            : 'Sign In'}
                    {!isForgotView && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                  </>
                )}
              </button>

              {!isForgotView && (mode === 'customer-login' || mode === 'delivery-login') &&
                !pendingFirstLoginUser && (
                <div className="pt-2">
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span>Sign in with</span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-5">
                    {socialProviders.map((provider) => (
                      <button
                        key={provider.label}
                        type="button"
                        onClick={() => handleProviderLogin(provider)}
                        title={provider.label}
                        className={`w-12 h-12 rounded-full border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all flex items-center justify-center text-xl font-bold ${provider.textClass}`}
                      >
                        <provider.Icon className="w-5 h-5" />
                      </button>
                    ))}
                  </div>
                  <p className="mt-4 text-center text-sm text-gray-400">
                    {mode === 'customer-login' ? (
                      <>
                        Don&apos;t have an account?{' '}
                        <button
                          type="button"
                          onClick={() => handleModeChange('signup')}
                          className="text-orange-500 font-semibold hover:text-orange-600"
                        >
                          Sign Up
                        </button>
                      </>
                    ) : (
                      <>
                        Don&apos;t have a delivery account?{' '}
                        <button
                          type="button"
                          onClick={() => handleModeChange('delivery-signup')}
                          className="text-orange-500 font-semibold hover:text-orange-600"
                        >
                          Delivery Sign Up
                        </button>
                      </>
                    )}
                  </p>
                </div>
              )}

              {!isForgotView && mode === 'signup' && !pendingFirstLoginUser && (
                <p className="pt-2 text-center text-sm text-gray-400">
                  Already have a customer account?{' '}
                  <button
                    type="button"
                    onClick={() => handleModeChange('customer-login')}
                    className="text-orange-500 font-semibold hover:text-orange-600"
                  >
                    Sign In
                  </button>
                </p>
              )}

              {!isForgotView && mode === 'delivery-signup' && !pendingFirstLoginUser && (
                <p className="pt-2 text-center text-sm text-gray-400">
                  Already have a delivery account?{' '}
                  <button
                    type="button"
                    onClick={() => handleModeChange('delivery-login')}
                    className="text-orange-500 font-semibold hover:text-orange-600"
                  >
                    Sign In
                  </button>
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
